import moment from "moment";

const DEFAULT_COOLOFF_MINUTES = 240;
const MAX_DATE_RANGE_DAYS = 30;

const PRIORITY_RANGES = {
    FLASH_SALE: { min: 51, max: 100 },
    DISCOUNTED_PRICING: { min: 101, max: 500 },
};

export const getCooloffMinutesFromConfigs = (configs) => {
    if (!Array.isArray(configs)) return DEFAULT_COOLOFF_MINUTES;

    const row = configs.find(
        (c) => String(c?.configKey || "").toLowerCase() === "cooloff_time_minutes"
    );

    const value = Number(row?.configValue);
    return Number.isFinite(value) && value >= 0 ? value : DEFAULT_COOLOFF_MINUTES;
};

export const isStopOnMatchEnabled = (configs) => {
    if (!Array.isArray(configs)) return false;

    const row = configs.find(
        (c) => String(c?.configKey || "").toLowerCase() === "stop_on_match_enabled"
    );

    return String(row?.configValue).toLowerCase() === "true";
};

const isOutcomeInvalid = (thenData, attributeConfig) => {
    if (!thenData?.subConditions?.length) return "No outcome added";

    let emptyWhenCount = 0;

    for (const block of thenData.subConditions) {
        const discount = block?.then?.discount;
        const discountInvalid =
            !discount?.discountType ||
            discount.discountPercentage === "" ||
            discount.discountPercentage === null ||
            discount.discountPercentage === undefined ||
            Number(discount.discountPercentage) <= 0;

        if (!block.when?.length) {
            emptyWhenCount += 1;
            if (discountInvalid) return "Empty outcome block must have a valid discount percentage";
            continue;
        }

        if (discountInvalid) return "Each outcome must have a valid discount percentage";

        const whenInvalid = block.when.some(cond => {
            if (!cond.attributeType) return true;
            if (!cond.attributeFilterType) return true;
            if (!cond.attributeFilterClause) return true;

            if (!hasValidConditionValue(cond, attributeConfig)) return true;

            return !cond.attributeFilterValue;
        });
        if (whenInvalid) return "Each Outcome condition must have valid values";
    }

    if (emptyWhenCount > 1) return "Only one outcome block can have no conditions";

    return null;
};

export const hasValidConditionValue = (condition, attributeConfig) => {
    const val = condition?.attributeFilterValue;
    if (!val) return true;
    const attributeMeta = attributeConfig.find(
        a => a.attributeType.toLowerCase() === condition.attributeType?.toLowerCase()
    );

    const fields = attributeMeta?.fields || [];

    const selectedFieldMeta = fields.find(
        f => f.name === condition.attributeFilterType
    );

    const operators = selectedFieldMeta?.operators || [];

    if (condition?.attributeFilterClause === "IN" && operators.length > 1) {
        if (!Array.isArray(val)) return false;
        return val.length !== 1;
    }
    return true;
};

export const hasSelectedBaseCondition = (conditions) => (
    conditions.some((c) => {
        const isBaseType = ["CATEGORY", "PRODUCT", "SKU"].includes(c.attributeType) || c.attributeFilterType === "customerGroups";
        const hasValue = Array.isArray(c.attributeFilterValue)
            ? c.attributeFilterValue.length > 0
            : c.attributeFilterValue !== "" && c.attributeFilterValue != null;
        return isBaseType && hasValue;
    })
);

const getSortedValidTimeSlots = (slots = []) => {
    const parsed = [];

    for (const slot of slots) {
        const start = moment(slot?.start, "HH:mm:ss", true);
        const end = moment(slot?.end, "HH:mm:ss", true);
        if (!start.isValid() || !end.isValid()) continue;

        parsed.push({ start, end });
    }

    return parsed.sort((a, b) => a.start.valueOf() - b.start.valueOf());
};

const getFrequencyError = (frequency, cooloffMinutes = DEFAULT_COOLOFF_MINUTES) => {
    if (!frequency) return false;

    let parsed = frequency;

    if (typeof frequency === "string") {
        try {
            parsed = JSON.parse(frequency);
        } catch {
            return "Each frequency day must have valid time slots";
        }
    }

    const days = Array.isArray(parsed) ? parsed : parsed?.daysOfWeek;
    if (!Array.isArray(days)) return false;
    if (days.length === 0) return false;

    const minGap = Number.isFinite(Number(cooloffMinutes)) ? Number(cooloffMinutes) : DEFAULT_COOLOFF_MINUTES;

    for (const day of days) {
        if (!day?.day) {
            return "Each frequency day must have a valid day selection";
        }

        const slots = Array.isArray(day?.timeSlots) ? day.timeSlots : [];
        if (!slots.length) {
            return "Each frequency day must have at least one time slot";
        }

        for (const slot of slots) {
            const start = slot?.start?.toString().trim();
            const end = slot?.end?.toString().trim();

            if (!start || !end) {
                return "Each frequency day must have valid time slots with start and end time";
            }

            const slotError = getTimeSlotError(slot, minGap);
            if (slotError) {
                return "For each frequency day, " + slotError;
            }
        }

        const sortedSlots = getSortedValidTimeSlots(slots);
        for (let i = 1; i < sortedSlots.length; i += 1) {
            const previousSlot = sortedSlots[i - 1];
            const currentSlot = sortedSlots[i];
            const gapMinutes = currentSlot.start.diff(previousSlot.end, "minutes");

            if (gapMinutes <= minGap) {
                return `For each frequency day, gap between consecutive slots must be at least ${minGap} minutes`;
            }
        }
    }

    return "";
};

export const validateRule = (form, attributeConfig, cooloffMinutes = DEFAULT_COOLOFF_MINUTES) => {
    const e = {};
    if (!form.ruleType) e.ruleType = "Rule type is required";
    if (!form.startDate) e.startDate = "Start date is required";
    if (!form.endDate) e.endDate = "End date is required";

    if (form.startDate && form.endDate && form.endDate < form.startDate) {
        e.endDate = "End date cannot be before start date";
    }

    if (!e.endDate && form.startDate && form.endDate) {
        if (!form.id) {
            if (moment(form.endDate).diff(moment(form.startDate), "days") > MAX_DATE_RANGE_DAYS) {
                e.endDate = `End date cannot be more than ${MAX_DATE_RANGE_DAYS} days after Start date`;
            }
        } else if (form.originalEndDate) {
            if (Math.abs(moment(form.endDate).diff(moment(form.originalEndDate), "days")) > MAX_DATE_RANGE_DAYS) {
                e.endDate = `End date cannot be changed by more than ${MAX_DATE_RANGE_DAYS} days from the original End date`;
            }
        }
    }

    if (form.ruleType === "FLASH_SALE" && (!form.frequency || (Array.isArray(form.frequency?.daysOfWeek) && form.frequency.daysOfWeek.length === 0))) {
        e.frequency = "Frequency is required for Flash Sale";
    }

    const frequencyError = getFrequencyError(form.frequency, cooloffMinutes);
    if (frequencyError) {
        e.frequency = frequencyError;
    }

    const priorityRange = PRIORITY_RANGES[form.ruleType];
    if (priorityRange) {
        const priority = Number(form.priority);
        if (form.priority === "" || form.priority === null || Number.isNaN(priority)) {
            e.priority = "Priority is required";
        } else if (priority < priorityRange.min || priority > priorityRange.max) {
            e.priority = `Priority must be between ${priorityRange.min} and ${priorityRange.max}`;
        }
    }

    if (!form.catalogId) e.catalogId = "Catalog is required";

    if (
        form.when.length === 0 ||
        !form.when.some(row => {
            const val = row.attributeFilterValue;
            if (Array.isArray(val)) return val.length > 0;
            if (typeof val === "object" && val !== null) return Object.keys(val).length > 0;
            if (typeof val === "string") return val.trim() !== "";
            return !!val;
        })
    ) {
        e.when = "At least one Rule Association condition with value is required";
    }

    if (!hasSelectedBaseCondition(form.when)) {
        e.when = "For Rule Association at least one CATEGORY/PRODUCT/SKU or customerGroups condition with value is required";
    }

    if (form.when.some(row => !hasValidConditionValue(row, attributeConfig))) {
        e.when = "For Rule Association condition with IN operator, select at least 2 values.";
    }

    const outcomeError = isOutcomeInvalid(form.then, attributeConfig);
    if (outcomeError) {
        e.then = outcomeError;
    }

    return e;
};

export const getTimeSlotError = (slot) => {
    if (!slot?.start || !slot?.end) return "";
    const startTime = moment(slot.start, "HH:mm:ss", true);
    const endTime = moment(slot.end, "HH:mm:ss", true);

    if (!startTime.isValid() || !endTime.isValid()) {
        return "Enter a valid start and end time";
    }

    if (!endTime.isAfter(startTime)) {
        return "End time must be greater than start time";
    }

    // if (endTime.diff(startTime, "minutes") < MIN_SLOT_DURATION_MINUTES) {
    //     return `End time must be at least ${MIN_SLOT_DURATION_MINUTES} minutes after start time`;
    // }

    return "";
};